import { useEffect, useRef } from 'react';

/**
 * useAutoRefresh — invoke `callback` every `intervalMs` while `enabled` is true.
 * Also fires `callback` once on every change to `triggers` (so initial mount
 * and filter changes work without needing a separate useEffect).
 *
 * setState calls inside `callback` happen via a ref so the timer is never
 * recreated when the callback identity changes (Dan Abramov pattern).
 */
export default function useAutoRefresh(callback, intervalMs, enabled, triggers = []) {
  const cbRef = useRef(callback);

  useEffect(() => {
    cbRef.current = callback;
  }, [callback]);

  // Fire immediately whenever any trigger changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    cbRef.current?.();
  }, triggers);

  // Periodic refresh while enabled
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => {
      cbRef.current?.();
    }, intervalMs);
    return () => clearInterval(id);
  }, [enabled, intervalMs]);
}
